import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth';
import { isNurse as isNurseUser } from '@/auth/roles';
import type { Staff } from '@/types/settings-api';
import { resolveEditAllowed } from '../resolve-edit-allowed';
import {
  useCreateStaff,
  useRolesList,
  useStaffById,
  useUpdateStaff,
} from '@/settings/services';
import { StaffEditShell, type StaffTabId } from './staff-edit-shell';
import { StaffPersonalTab } from './tabs/staff-personal-tab';
import { StaffAccountTab } from './tabs/staff-account-tab';
import { StaffProfessionalTab } from './tabs/staff-professional-tab';
import { StaffScheduleTab } from './tabs/staff-schedule-tab';
import { StaffPracticesTab } from './tabs/staff-practices-tab';
import { MacroTab } from '../macro/macro-tab';
import {
  emptyStaff,
  isStaffDirty,
  normaliseStaffForSave,
} from './staff-edit-merge';

/**
 * Staff create / edit page (PROD-765).
 *
 * Full-page route at `/settings/staff/:staffSetupId` (or `/settings/staff/new`
 * for create). Mirrors Flutter `staff_base_widget.dart`. Renders OUTSIDE the
 * SettingsShell chrome — the standard settings tab navbar is replaced by this
 * page's own breadcrumb + per-tab navigation.
 *
 * Phased delivery on the prod-765 branch (per scoping with @andrew):
 *   Commit 1 (this commit): route + page shell + Personal + Account tabs.
 *   Commit 2 (next):        Professional + Schedule tabs (nurse-only).
 *   Commit 3 (after):       Practices + Macro tabs.
 *
 * Tab visibility (Flutter `staff_base_widget.dart:107-156`):
 *   Always:      Personal Details, Account Details, Practices
 *   Nurse only:  Professional Details, Schedule, Macro
 *   (`isNurse` from the staff member's `role` looked up in `useRolesList`.)
 */
export interface StaffEditPageProps {
  mode: 'create' | 'edit';
  /**
   * Account Settings mode (Flutter `isFromAccountSettings`, PROD-863 follow-up).
   * The logged-in staff member edits their OWN record. Set by
   * `AccountSettingsRoute`, never by the admin list routes.
   */
  accountMode?: boolean;
  /**
   * In account mode the id comes from the auth user (`userData.id`), not the
   * `:staffSetupId` route param. Ignored unless `accountMode` is true.
   */
  idOverride?: string;
}

export function StaffEditPage({
  mode,
  accountMode = false,
  idOverride,
}: StaffEditPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams<{ staffSetupId: string }>();
  const staffSetupId =
    mode === 'edit' ? (accountMode ? idOverride : params.staffSetupId) : undefined;

  const authUser = useAuthStore((s) => s.user);
  const orgId = authUser?.organizationId;
  const canEdit = usePermission(['Manage_Staff'], { edit: true });
  const canAdd = usePermission(['Manage_Staff'], { add: true });
  const canDelete = usePermission(['Manage_Staff'], { delete: true });
  // Archive lives on the Account tab (Flutter `staff_account_tab.dart:649`):
  // existing record + Manage_Staff delete, and never in Account Settings
  // self-edit (Flutter renders the simplified layout there).
  const canArchive = mode === 'edit' && !accountMode && canDelete;
  // Self-edit is always allowed in account mode regardless of Manage_Staff —
  // see resolveEditAllowed (mirrors the Flutter `... || isFromAccountSettings`
  // save guard, staff_base_widget.dart:173,235,245).
  const isAllowed = resolveEditAllowed({ mode, accountMode, canAdd, canEdit });

  const { data: roles = [] } = useRolesList(orgId);
  const { data: existingStaff, isLoading: isStaffLoading } =
    useStaffById(staffSetupId);

  const createMutation = useCreateStaff();
  const updateMutation = useUpdateStaff();

  // Source of truth — what the server returned (or a fresh blank in create
  // mode). Draft starts equal to source and mutates as the user edits.
  const [source, setSource] = useState<Staff | null>(null);
  const [draft, setDraft] = useState<Staff | null>(null);
  const [activeTab, setActiveTab] = useState<StaffTabId>('personal');

  // Seed source/draft from the data we should be showing, adjusting state
  // *during render* rather than in an effect (React's "adjusting state when a
  // prop changes" pattern — avoids the synchronous-setState-in-effect
  // cascading-render warning, PROD-967). `seedKey` identifies what we last
  // seeded from: the constant 'create' (one-shot blank) in create mode, or the
  // loaded record's id in edit mode (re-seeds if the route points at a
  // different staff member). Guarded so it runs once per source change and
  // converges (seededKey catches up immediately).
  const [seededKey, setSeededKey] = useState<string | null>(null);
  const seedKey =
    mode === 'create' ? 'create' : (existingStaff?.staffSetupId ?? null);
  if (seedKey !== null && seedKey !== seededKey) {
    const seed = mode === 'create' ? emptyStaff() : existingStaff!;
    setSeededKey(seedKey);
    setSource(seed);
    setDraft(seed);
  }

  const isDirty = useMemo(
    () => (source && draft ? isStaffDirty(source, draft) : false),
    [source, draft],
  );

  // Nurse-tab gating: look up the staff member's role in the roles list and
  // honour its `isNurse` flag. Defaults to false until both are loaded so the
  // initial render shows the 3-tab variant.
  // In account mode Flutter ORs an extra branch: the logged-in user's own
  // tenant role is a nurse (`isFromAccountSettings && userData.tenantRoleId ==
  // nurseTenantRole`, staff_base_widget.dart:115-116). `nurseTenantRole = "4"`
  // (app_constants.dart:726) — a tenantRoleId, distinct from the numeric role.
  // Uses the shared `isNurse()` gate rather than an inline `tenantRoleId === "4"`
  // test, so account mode cannot drift from the app's other nurse gates; the
  // gate also requires `role === "2"` and coerces numeric wire values.
  // Plain computation (no useMemo) — a `.find` over the small roles list is
  // cheap, and the React Compiler memoizes it automatically.
  const isNurseByTenant = accountMode && isNurseUser(authUser);
  const isNurse =
    isNurseByTenant ||
    (!!draft?.role &&
      roles.find((r) => r.roleId === draft.role)?.isNurse === true);

  // v1 validation — the core required-fields contract for save-button enable.
  // Per-tab inline errors render on the relevant field.
  // Phone bounds match the Practitioner Create flow (PR #340 Samer review):
  // CountryDialPicker writes "+CC-digits"; count the digit half and gate
  // against the ITU-T E.164 spec range [7, 15]. Floor rejects obvious
  // placeholders; ceiling rejects overflow. Mirrors Flutter `add_edit_staff_widget.dart`
  // which gates Save on a non-blank phone digits component.
  const phoneDigits = draft
    ? (draft.mobileNumber.split('-')[1] ?? '').replace(/\D/g, '')
    : '';
  const isValid =
    !!draft &&
    draft.firstName.trim().length >= 3 &&
    draft.lastName.trim().length >= 3 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.emailId.trim()) &&
    phoneDigits.length >= 7 &&
    phoneDigits.length <= 15 &&
    draft.role !== null;

  const handleSave = () => {
    if (!draft || !source) return;
    if (!isDirty) return;
    if (!isValid) {
      toast.error('Please complete the required fields.');
      return;
    }
    const payload = normaliseStaffForSave(draft);
    const onSuccess = () => {
      // Invalidate explicitly — useCreateStaff / useUpdateStaff no longer
      // auto-invalidate per the Samer pattern locked in PR #323 (caller
      // owns post-success refetch so embedded Sheets can defer until
      // unmount). This route navigates away on success so the refetch
      // happens on the destination page's mount.
      queryClient.invalidateQueries({ queryKey: ['settings', 'staff-list'] });
      if (mode === 'edit' && draft.staffSetupId) {
        queryClient.invalidateQueries({
          queryKey: ['settings', 'staff-by-id', draft.staffSetupId],
        });
      }
      toast.success(
        mode === 'create'
          ? `Created ${payload.firstName} ${payload.lastName}`.trim()
          : `Saved ${payload.firstName} ${payload.lastName}`.trim(),
      );
      // Account mode is a top-level self-edit destination, not a drill-down
      // from the Staff list (which the user may not even have access to) — go
      // home on success instead of the admin list.
      navigate(accountMode ? '/' : '/settings/staff');
    };
    const onError = (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to save staff';
      toast.error(message);
    };
    if (mode === 'create') {
      createMutation.mutate(payload, { onSuccess, onError });
    } else {
      updateMutation.mutate(payload, { onSuccess, onError });
    }
  };

  const isPending =
    mode === 'create' ? createMutation.isPending : updateMutation.isPending;

  // PROD-788: Professional Details tab owns its own POST endpoint
  // (`/api/Staff/ProfessionalDetails`), separate from the staff PUT. When
  // that tab is active and has registered a save handle, chrome Save
  // dispatches the tab's handler instead of `handleSave` (which fires the
  // staff PUT). Same pattern as PractitionerSaveContext (PR #340) but
  // routed via a callback prop since Staff has fewer tabs to wire.
  const [profTabSave, setProfTabSave] =
    useState<import('./tabs/staff-professional-tab').TabSaveHandle | null>(
      null,
    );
  const profTabActive = activeTab === 'professional';

  const chromeSaveLabel = profTabActive && profTabSave
    ? (profTabSave.label ?? 'Save')
    : mode === 'create'
      ? 'Send Invite'
      : 'Save';
  const chromeSaveDisabled = profTabActive && profTabSave
    ? !profTabSave.canSave || profTabSave.isPending
    : !isDirty || !isValid || !isAllowed || isPending;
  const chromeOnSave = profTabActive && profTabSave
    ? profTabSave.save
    : handleSave;

  return (
    <StaffEditShell
      mode={mode}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isNurse={isNurse}
      staffName={draftDisplayName(draft, existingStaff?.firstName, existingStaff?.lastName)}
      photoUrl={draft?.photoURL ?? null}
      accountMode={accountMode}
      onBack={() => navigate(accountMode ? '/' : '/settings/staff')}
      saveLabel={chromeSaveLabel}
      onSave={chromeOnSave}
      saveDisabled={chromeSaveDisabled}
      isLoading={mode === 'edit' && isStaffLoading && !draft}
    >
      {!draft ? null : activeTab === 'personal' ? (
        <StaffPersonalTab draft={draft} onChange={setDraft} canEdit={isAllowed} />
      ) : activeTab === 'account' ? (
        <StaffAccountTab
          draft={draft}
          onChange={setDraft}
          canEdit={isAllowed}
          isCreate={mode === 'create'}
          canArchive={canArchive}
          onArchived={() => navigate('/settings/staff')}
          accountMode={accountMode}
        />
      ) : activeTab === 'professional' ? (
        <StaffProfessionalTab
          staffSetupId={draft.staffSetupId}
          canEdit={isAllowed}
          onRegisterSave={setProfTabSave}
        />
      ) : activeTab === 'schedule' ? (
        <StaffScheduleTab draft={draft} />
      ) : activeTab === 'practices' ? (
        <StaffPracticesTab draft={draft} />
      ) : (
        <MacroTab
          providerId={draft.staffSetupId ?? ''}
          organizationId={orgId ?? null}
        />
      )}
    </StaffEditShell>
  );
}

function draftDisplayName(
  draft: Staff | null,
  fallbackFirst?: string,
  fallbackLast?: string,
): string {
  const first = (draft?.firstName ?? fallbackFirst ?? '').trim();
  const last = (draft?.lastName ?? fallbackLast ?? '').trim();
  const combined = `${first} ${last}`.trim();
  return combined.length > 0 ? combined : 'New staff member';
}

